import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import ImageExt from "@tiptap/extension-image";
import LinkExt from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import Color from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import {
  Plus, Trash2, Edit2, Send, Clock, Eye, X, Users, FileText, Calendar,
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight, Image, Link as LinkIcon, Table as TableIcon,
  Type, Heading1, Heading2, Heading3, Undo, Redo, Palette
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  draft: { label: "Brouillon", color: "bg-slate-500/20 text-slate-300" },
  scheduled: { label: "Programmee", color: "bg-yellow-500/20 text-yellow-300" },
  sent: { label: "Envoyee", color: "bg-green-500/20 text-green-300" },
  failed: { label: "Echec", color: "bg-red-500/20 text-red-300" },
};

// Toolbar button
const TBtn = ({ onClick, active, children, title }) => (
  <button type="button" onClick={onClick} title={title}
    className={`p-1.5 rounded ${active ? "bg-[#2395E3]/30 text-[#2395E3]" : "text-slate-500 hover:text-slate-800 hover:bg-gray-100"}`}>
    {children}
  </button>
);

// Rich text editor toolbar
const EditorToolbar = ({ editor }) => {
  if (!editor) return null;

  const addImage = () => {
    const url = window.prompt("URL de l'image:");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  const addLink = () => {
    const url = window.prompt("URL du lien:");
    if (url) editor.chain().focus().setLink({ href: url }).run();
  };

  const addTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  return (
    <div className="flex flex-wrap gap-0.5 p-2 border-b border-gray-200 bg-gray-50 rounded-t-lg">
      <TBtn onClick={() => editor.chain().focus().undo().run()} title="Annuler"><Undo className="w-4 h-4" /></TBtn>
      <TBtn onClick={() => editor.chain().focus().redo().run()} title="Refaire"><Redo className="w-4 h-4" /></TBtn>
      <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
      <TBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title="Titre 1"><Heading1 className="w-4 h-4" /></TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Titre 2"><Heading2 className="w-4 h-4" /></TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Titre 3"><Heading3 className="w-4 h-4" /></TBtn>
      <TBtn onClick={() => editor.chain().focus().setParagraph().run()} active={editor.isActive("paragraph")} title="Paragraphe"><Type className="w-4 h-4" /></TBtn>
      <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
      <TBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Gras"><Bold className="w-4 h-4" /></TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italique"><Italic className="w-4 h-4" /></TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Souligne"><UnderlineIcon className="w-4 h-4" /></TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Barre"><Strikethrough className="w-4 h-4" /></TBtn>
      <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
      <TBtn onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Aligner gauche"><AlignLeft className="w-4 h-4" /></TBtn>
      <TBtn onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Centrer"><AlignCenter className="w-4 h-4" /></TBtn>
      <TBtn onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Aligner droite"><AlignRight className="w-4 h-4" /></TBtn>
      <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
      <TBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Liste a puces"><List className="w-4 h-4" /></TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Liste numerotee"><ListOrdered className="w-4 h-4" /></TBtn>
      <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
      <TBtn onClick={addLink} active={editor.isActive("link")} title="Lien"><LinkIcon className="w-4 h-4" /></TBtn>
      <TBtn onClick={addImage} title="Image"><Image className="w-4 h-4" /></TBtn>
      <TBtn onClick={addTable} title="Tableau"><TableIcon className="w-4 h-4" /></TBtn>
      <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
      <label className="p-1.5 rounded text-slate-500 hover:text-slate-800 hover:bg-gray-100 cursor-pointer" title="Couleur du texte">
        <Palette className="w-4 h-4" />
        <input type="color" className="sr-only" onChange={(e) => editor.chain().focus().setColor(e.target.value).run()} />
      </label>
    </div>
  );
};

// Tiptap Editor Wrapper
const RichEditor = ({ content, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      ImageExt.configure({ inline: true }),
      LinkExt.configure({ openOnClick: false }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    content: content || "",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Update content when prop changes (e.g., editing a different campaign)
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || "");
    }
  }, [content]);

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <EditorToolbar editor={editor} />
      <EditorContent editor={editor} className="newsletter-editor-content" />
    </div>
  );
};

const NewsletterManager = ({ headers }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showSchedule, setShowSchedule] = useState(null);
  const [scheduleDate, setScheduleDate] = useState("");
  const [sending, setSending] = useState(null);
  const [form, setForm] = useState({ subject: "", html_content: "", status: "draft", scheduled_at: "" });

  const fetchAll = async () => {
    try {
      const [c, s] = await Promise.all([
        axios.get(`${API}/admin/newsletters`, { headers }),
        axios.get(`${API}/admin/newsletter-subscribers`, { headers }),
      ]);
      setCampaigns(c.data);
      setSubscribers(s.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchAll(); }, []);

  const resetForm = () => { setForm({ subject: "", html_content: "", status: "draft", scheduled_at: "" }); setEditingId(null); setShowForm(false); };

  const handleEdit = (c) => { setForm({ subject: c.subject, html_content: c.html_content || "", status: c.status || "draft", scheduled_at: c.scheduled_at || "" }); setEditingId(c.id); setShowForm(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/admin/newsletters/${editingId}`, form, { headers });
      } else {
        await axios.post(`${API}/admin/newsletters`, form, { headers });
      }
      resetForm();
      fetchAll();
    } catch (err) { console.error(err); }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm("Supprimer cette campagne ?")) return;
    await axios.delete(`${API}/admin/newsletters/${id}`, { headers });
    fetchAll();
  };

  const sendNow = async (id) => {
    if (!window.confirm(`Envoyer cette newsletter a ${subscribers.length} abonne(s) maintenant ?`)) return;
    setSending(id);
    try {
      const res = await axios.post(`${API}/admin/newsletters/${id}/send`, {}, { headers });
      alert(`Envoi termine: ${res.data.sent_count}/${res.data.total} email(s) envoye(s)${res.data.errors?.length ? "\nErreurs: " + res.data.errors.join(", ") : ""}`);
      fetchAll();
    } catch (err) {
      alert("Erreur: " + (err.response?.data?.detail || err.message));
    }
    setSending(null);
  };

  const scheduleNewsletter = async (id) => {
    if (!scheduleDate) return;
    try {
      await axios.post(`${API}/admin/newsletters/${id}/schedule`, { scheduled_at: scheduleDate }, { headers });
      setShowSchedule(null);
      setScheduleDate("");
      fetchAll();
    } catch (err) { alert("Erreur: " + (err.response?.data?.detail || err.message)); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold" data-testid="newsletter-title">Campagnes Newsletter</h1>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} className="flex items-center gap-2 px-4 py-2 btn-primary rounded-lg" data-testid="add-newsletter-btn">
          <Plus className="w-5 h-5" /> Nouvelle campagne
        </button>
      </div>

      {/* Stats */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1 p-4 rounded-xl bg-[#131B2F] border border-white/10 text-center">
          <div className="text-2xl font-bold text-[#2395E3]">{subscribers.length}</div>
          <div className="text-xs text-slate-400">Abonnes</div>
        </div>
        <div className="flex-1 p-4 rounded-xl bg-[#131B2F] border border-white/10 text-center">
          <div className="text-2xl font-bold text-[#00CCC2]">{campaigns.length}</div>
          <div className="text-xs text-slate-400">Campagnes</div>
        </div>
        <div className="flex-1 p-4 rounded-xl bg-[#131B2F] border border-white/10 text-center">
          <div className="text-2xl font-bold text-green-400">{campaigns.filter(c => c.status === "sent").length}</div>
          <div className="text-xs text-slate-400">Envoyees</div>
        </div>
        <div className="flex-1 p-4 rounded-xl bg-[#131B2F] border border-white/10 text-center">
          <div className="text-2xl font-bold text-yellow-400">{campaigns.filter(c => c.status === "scheduled").length}</div>
          <div className="text-xs text-slate-400">Programmees</div>
        </div>
      </div>

      {/* Subscribers */}
      {subscribers.length > 0 && (
        <details className="mb-6 rounded-xl bg-[#131B2F] border border-white/10 overflow-hidden">
          <summary className="p-4 cursor-pointer flex items-center gap-2 text-sm font-semibold text-slate-300">
            <Users className="w-4 h-4 text-[#2395E3]" /> {subscribers.length} abonne(s)
          </summary>
          <div className="px-4 pb-4 max-h-48 overflow-y-auto">
            <div className="flex flex-wrap gap-2">
              {subscribers.map((s, i) => (
                <span key={i} className="px-3 py-1 text-xs rounded-full bg-[#0B1121] text-slate-300">{s.email}</span>
              ))}
            </div>
          </div>
        </details>
      )}

      {/* Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="mb-8 p-6 rounded-xl bg-[#131B2F] border border-white/10 space-y-4" data-testid="newsletter-form">
          <input type="text" placeholder="Objet de la newsletter *" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none text-lg" data-testid="newsletter-subject" />
          <div>
            <label className="text-sm text-slate-400 block mb-2">Contenu</label>
            <RichEditor content={form.html_content} onChange={(html) => setForm(prev => ({ ...prev, html_content: html }))} />
          </div>
          <div className="flex gap-4">
            <button type="submit" className="px-6 py-2 btn-primary rounded-lg" data-testid="newsletter-submit">
              {editingId ? "Mettre a jour" : "Enregistrer le brouillon"}
            </button>
            <button type="button" onClick={resetForm} className="px-6 py-2 rounded-lg border border-white/20">Annuler</button>
          </div>
        </form>
      )}

      {/* Campaigns list */}
      <div className="space-y-4">
        {campaigns.map((c) => {
          const st = STATUS_LABELS[c.status] || STATUS_LABELS.draft;
          return (
            <div key={c.id} className="p-4 rounded-xl bg-[#131B2F] border border-white/10">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-[#0B1121]">
                  <FileText className="w-5 h-5 text-[#2395E3]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate">{c.subject}</h3>
                  <div className="flex gap-3 text-xs text-slate-400 mt-1">
                    <span>{new Date(c.created_at).toLocaleDateString("fr-FR")}</span>
                    {c.recipients_count > 0 && <span>{c.recipients_count} destinataire(s)</span>}
                    {c.sent_at && <span>Envoyee le {new Date(c.sent_at).toLocaleString("fr-FR")}</span>}
                    {c.status === "scheduled" && c.scheduled_at && <span>Prog: {new Date(c.scheduled_at).toLocaleString("fr-FR")}</span>}
                  </div>
                </div>
                <span className={`px-3 py-1 text-xs rounded-full ${st.color}`}>{st.label}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setPreview(c)} className="p-2 hover:bg-white/10 rounded-lg" title="Apercu"><Eye className="w-4 h-4 text-slate-400" /></button>
                  {c.status !== "sent" && (
                    <>
                      <button onClick={() => handleEdit(c)} className="p-2 hover:bg-[#00CCC2]/20 rounded-lg" title="Modifier"><Edit2 className="w-4 h-4 text-[#00CCC2]" /></button>
                      <button onClick={() => { setShowSchedule(showSchedule === c.id ? null : c.id); setScheduleDate(""); }} className="p-2 hover:bg-yellow-500/20 rounded-lg" title="Programmer"><Clock className="w-4 h-4 text-yellow-400" /></button>
                      <button onClick={() => sendNow(c.id)} disabled={sending === c.id} className="p-2 hover:bg-[#2395E3]/20 rounded-lg" title="Envoyer">
                        <Send className={`w-4 h-4 ${sending === c.id ? "text-slate-500 animate-pulse" : "text-[#2395E3]"}`} />
                      </button>
                    </>
                  )}
                  <button onClick={() => deleteCampaign(c.id)} className="p-2 hover:bg-red-500/20 rounded-lg"><Trash2 className="w-4 h-4 text-red-400" /></button>
                </div>
              </div>
              {showSchedule === c.id && (
                <div className="mt-3 p-3 rounded-lg bg-[#0B1121] flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-yellow-400" />
                  <input type="datetime-local" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} className="px-3 py-2 rounded-lg bg-[#131B2F] border border-white/10 outline-none text-sm" />
                  <button onClick={() => scheduleNewsletter(c.id)} className="px-4 py-2 btn-primary rounded-lg text-sm">Programmer</button>
                  <button onClick={() => setShowSchedule(null)} className="px-4 py-2 rounded-lg border border-white/20 text-sm">Annuler</button>
                </div>
              )}
            </div>
          );
        })}
        {campaigns.length === 0 && <p className="text-slate-500 text-center py-8">Aucune campagne. Creez votre premiere newsletter !</p>}
      </div>

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-gray-800">{preview.subject}</h3>
              <button onClick={() => setPreview(null)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-6 prose max-w-none" dangerouslySetInnerHTML={{ __html: preview.html_content }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsletterManager;
